import type { QueryStatus } from "@tanstack/react-query";
import { classifyError } from "@/lib/errors/ErrorClassifier";

/**
 * Sous-ensemble de `UseQueryResult` nécessaire à la décision d'affichage.
 * On ne dépend que de ces 3 champs → testable sans TanStack ni React.
 * `status` est typé sur l'union source de TanStack (`QueryStatus`) : un renommage
 * futur (cf. v4 `loading` → v5 `pending`) casse à la compilation plutôt qu'en
 * silence.
 */
export interface BootstrapQueryState {
  status: QueryStatus;
  data: unknown;
  error: unknown;
}

/**
 * Lit `statusCode` sur une `ApiError` sans élargir le type.
 * `apiClient` pose ce champ sur l'erreur qu'il throw.
 */
function extractStatusCode(error: unknown): number | undefined {
  if (error && typeof error === "object" && "statusCode" in error) {
    const statusCode = (error as { statusCode: unknown }).statusCode;
    if (typeof statusCode === "number") return statusCode;
  }
  return undefined;
}

/**
 * Décide d'afficher la page plein écran « serveur injoignable ».
 *
 * Vrai uniquement quand la requête de bootstrap (`/cabinet-members/me`) :
 *  1. est en erreur définitive (retries épuisés) — `status === 'error'` ;
 *  2. n'a JAMAIS abouti — `data === undefined`. TanStack Query conserve la
 *     dernière donnée de succès : une panne survenant APRÈS un premier succès
 *     garde `data` défini → on ne prend PAS le contrôle de l'écran (ça reste un
 *     toast). C'est exactement ce qui encode « au chargement uniquement » ;
 *  3. n'est PAS une réponse serveur 4xx. Tout `statusCode` dans 400–499 (401/403
 *     auth, 404/409/422/429 métier) signifie que le serveur a répondu → jamais
 *     une indisponibilité. On exclut ces codes AVANT `classifyError`, dont la
 *     branche `!navigator.onLine` (ErrorClassifier.ts) reclasserait sinon
 *     n'importe quelle erreur en `network` quand le navigateur se croit hors-ligne
 *     (faux positif connu sur certains VPN/Wi-Fi). Restent : aucune réponse
 *     (fetch échoué, `statusCode` absent ou 0) ou 5xx → catégorie `network`.
 */
export function shouldShowServerUnavailable(query: BootstrapQueryState): boolean {
  if (query.status !== "error") return false;
  if (query.data !== undefined) return false;

  const statusCode = extractStatusCode(query.error);
  // 4xx = le serveur a répondu → ce n'est pas une panne serveur (0 et 5xx passent).
  if (statusCode !== undefined && statusCode >= 400 && statusCode < 500) {
    return false;
  }

  return classifyError(query.error, statusCode).category === "network";
}
